'use client';

import { LayoutNodeStyleMap, type LayoutNode } from 'shop-ui/types/basic';
import omit from 'lodash-es/omit';
import { ELEMENT_TYPE, FEATURE_FLAG_KEYS } from 'shop-ui/constants';
import { getElementUlId } from 'app/shop-ui/tools/getElementUlId';
import i18n from '@shared/i18n';
import { GRID_LAYOUT_TYPE } from './rules';

const config: LayoutNode = {
    id: '',
    type: '' as ELEMENT_TYPE,
    name: '',
    enable: true,
    next: null,
    deletable: true,
    editable: true,
};

export const headerLayoutFeatures = [
    FEATURE_FLAG_KEYS.HEADER_LAYOUT,
    FEATURE_FLAG_KEYS.RESIZE_HANDLE,
    FEATURE_FLAG_KEYS.SPACING_HANDLE,
    FEATURE_FLAG_KEYS.BACKGROUND_COLOR,
    /*
        暫時移除 header 能夠設定的背景樣式
        相關討論：https://app.asana.com/1/5180948870484/project/1209320197619741/task/1210758238409683?focus=true
    */
    // FEATURE_FLAG_KEYS.BACKGROUND_TYPE,
    // FEATURE_FLAG_KEYS.BACKGROUND_COLOR_GRADIENT,
    // FEATURE_FLAG_KEYS.BACKGROUND_IMAGE,
    // FEATURE_FLAG_KEYS.BACKGROUND_VIDEO,
    // FEATURE_FLAG_KEYS.IMAGE_FIT,
    // FEATURE_FLAG_KEYS.IMAGE_POSITION,
    FEATURE_FLAG_KEYS.GRID_GAP,
    FEATURE_FLAG_KEYS.BORDER_RADIUS,
    FEATURE_FLAG_KEYS.MIN_HEIGHT,
    FEATURE_FLAG_KEYS.FIXED_SCREEN_HEIGHT,
    FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL,
];

export const primaryLevelGridFeatures = [
    FEATURE_FLAG_KEYS.GRID,
    FEATURE_FLAG_KEYS.RESIZE_HANDLE,
    FEATURE_FLAG_KEYS.SPACING_HANDLE,
    FEATURE_FLAG_KEYS.BACKGROUND_COLOR,
    FEATURE_FLAG_KEYS.BACKGROUND_TYPE,
    FEATURE_FLAG_KEYS.BACKGROUND_COLOR_GRADIENT,
    FEATURE_FLAG_KEYS.BACKGROUND_IMAGE,
    FEATURE_FLAG_KEYS.BACKGROUND_VIDEO,
    FEATURE_FLAG_KEYS.BACKGROUND_MASK_COLOR,
    FEATURE_FLAG_KEYS.IMAGE_FIT,
    FEATURE_FLAG_KEYS.IMAGE_POSITION,
    FEATURE_FLAG_KEYS.GRID_GAP,
    FEATURE_FLAG_KEYS.ROTATE,
    FEATURE_FLAG_KEYS.CELL_WIDTH,
    FEATURE_FLAG_KEYS.MIN_HEIGHT,
    FEATURE_FLAG_KEYS.FIXED_SCREEN_HEIGHT,
    FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL,
    FEATURE_FLAG_KEYS.ANIMATION,
];

export const secondaryLevelGridFeatures = [
    FEATURE_FLAG_KEYS.GRID,
    FEATURE_FLAG_KEYS.SPACING_HANDLE,
    FEATURE_FLAG_KEYS.BACKGROUND_COLOR,
    FEATURE_FLAG_KEYS.BACKGROUND_TYPE,
    FEATURE_FLAG_KEYS.BACKGROUND_COLOR_GRADIENT,
    FEATURE_FLAG_KEYS.BACKGROUND_IMAGE,
    FEATURE_FLAG_KEYS.BACKGROUND_VIDEO,
    FEATURE_FLAG_KEYS.BACKGROUND_MASK_COLOR,
    FEATURE_FLAG_KEYS.IMAGE_FIT,
    FEATURE_FLAG_KEYS.IMAGE_POSITION,
    FEATURE_FLAG_KEYS.GRID_GAP,
    FEATURE_FLAG_KEYS.BORDER_RADIUS,
    FEATURE_FLAG_KEYS.GRID_ALIGNMENT,
    FEATURE_FLAG_KEYS.IMAGE_URL,
    FEATURE_FLAG_KEYS.VIDEO_URL,
    FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL,
];

export const innerSecondaryLevelGridFeatures = [
    FEATURE_FLAG_KEYS.BACKGROUND_COLOR,
    FEATURE_FLAG_KEYS.BACKGROUND_TYPE,
    FEATURE_FLAG_KEYS.BACKGROUND_COLOR_GRADIENT,
    FEATURE_FLAG_KEYS.BACKGROUND_IMAGE,
    FEATURE_FLAG_KEYS.BACKGROUND_VIDEO,
    FEATURE_FLAG_KEYS.BACKGROUND_MASK_COLOR,
    FEATURE_FLAG_KEYS.IMAGE_FIT,
    FEATURE_FLAG_KEYS.IMAGE_POSITION,
    FEATURE_FLAG_KEYS.BORDER_RADIUS,
    FEATURE_FLAG_KEYS.GRID_GAP,
    FEATURE_FLAG_KEYS.HYPERLINK,
    FEATURE_FLAG_KEYS.IMAGE_URL,
    FEATURE_FLAG_KEYS.VIDEO_URL,
    FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL,
];

/*
    暫時移除 header 能夠設定的背景樣式
    相關討論：https://app.asana.com/1/5180948870484/project/1209320197619741/task/1210758238409683?focus=true
*/
const defaultStyle = {
    [FEATURE_FLAG_KEYS.BACKGROUND_TYPE]: 'color',
    // [FEATURE_FLAG_KEYS.BACKGROUND_COLOR_GRADIENT]: true,
    // [FEATURE_FLAG_KEYS.BACKGROUND_IMAGE]: true,
    // [FEATURE_FLAG_KEYS.IMAGE_FIT]: 'cover',
    // [FEATURE_FLAG_KEYS.IMAGE_POSITION]: 'center',
    // [FEATURE_FLAG_KEYS.BACKGROUND_VIDEO]: true,
} as const;

function primaryGrid(
    name: string = i18n.gettext('區塊'),
    gridType: GRID_LAYOUT_TYPE = GRID_LAYOUT_TYPE.SINGLE_CELL,
    editable: boolean = true,
    deletable: boolean = true,
    presetStyle: LayoutNodeStyleMap[string] = {}
) {
    const id = getElementUlId();

    const layout = {
        ...config,
        type: ELEMENT_TYPE.GRID_PRIMARY_LEVEL,
        id,
        name,
        editable,
        deletable,
    };

    return {
        id,
        layout,
        style: {
            ...defaultStyle,
            [FEATURE_FLAG_KEYS.GRID]: gridType,
            [FEATURE_FLAG_KEYS.MIN_HEIGHT]: '5vh',
            [FEATURE_FLAG_KEYS.CELL_WIDTH]: '60vw',
            [FEATURE_FLAG_KEYS.FIXED_SCREEN_HEIGHT]: false,
            [FEATURE_FLAG_KEYS.PADDING_LEFT]: '27px',
            [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '27px',
            ...presetStyle,
        },
    };
}

function secondaryGrid(
    cellNumber: number,
    gridType: GRID_LAYOUT_TYPE = GRID_LAYOUT_TYPE.SINGLE_CELL,
    presetStyle: LayoutNodeStyleMap[string] = {}
) {
    const id = getElementUlId();

    const layout = {
        ...omit(config, 'deletable'),
        type: ELEMENT_TYPE.GRID_SECONDARY_LEVEL,
        id,
        name: `cell-${cellNumber}:level-2-grid`,
    };

    return {
        id,
        layout,
        style: {
            ...defaultStyle,
            [FEATURE_FLAG_KEYS.GRID]: gridType,
            ...presetStyle,
        },
    };
}

function secondaryInner(cellNumber: number) {
    const id = getElementUlId();

    const layout = {
        ...omit(config, 'deletable'),
        type: ELEMENT_TYPE.GRID_SECONDARY_LEVEL_INNER,
        id,
        name: `cell-${cellNumber}:level-2-grid-inner`,
    };

    return {
        id,
        layout,
        style: {},
    };
}

export const generate = {
    primary: primaryGrid,
    secondary: secondaryGrid,
    secondaryInner,
};
